import { supabase } from "@/lib/supabase";

// Common studio routes to warm-cache after initial load.
// These map to the lazy chunks generated by Vite for each page.
// Calling import() triggers the fetch; the SW intercepts it and caches the chunk.
const STUDIO_PREFETCH_MODULES = [
  () => import("@/pages/studio/dashboard/page.tsx"),
  () => import("@/pages/studio/editor/page.tsx"),
  () => import("@/pages/studio/analytics/page.tsx"),
  () => import("@/pages/studio/pipeline/page.tsx"),
  () => import("@/pages/studio/calendar/page.tsx"),
  () => import("@/pages/studio/briefs/page.tsx"),
  () => import("@/pages/studio/assets/page.tsx"),
  () => import("@/pages/studio/campaigns/page.tsx"),
  () => import("@/pages/studio/library/page.tsx"),
];

const PREFETCH_DELAY_MS = 2500;
const PREFETCH_KEY = "easel-sw-last-prefetch";

function shouldRun(): boolean {
  if (typeof window === "undefined") return false;
  if (!("serviceWorker" in navigator)) return false;
  if (!import.meta.env.PROD) return false;

  // Only prefetch studio chunks when the user is actually in the studio,
  // or has visited it before in this session.
  const path = window.location.pathname;
  const isStudio = path.startsWith("/studio");
  const hasVisitedStudio = sessionStorage.getItem("easel-studio-visited") === "true";

  if (!isStudio && !hasVisitedStudio) return false;

  // Throttle: only prefetch once per 10-minute window per session
  const lastPrefetch = sessionStorage.getItem(PREFETCH_KEY);
  if (lastPrefetch) {
    const elapsed = Date.now() - parseInt(lastPrefetch, 10);
    if (elapsed < 10 * 60 * 1000) return false;
  }

  return true;
}

async function doPrefetch(): Promise<void> {
  try {
    // Fire-and-forget background imports. Vite resolves the hashed chunk URLs;
    // the SW fetch handler intercepts the network requests and caches them.
    STUDIO_PREFETCH_MODULES.forEach((fn) => {
      try {
        fn();
      } catch {
        // Silently ignore failed prefetches
      }
    });

    // Also ask the SW to explicitly cache any shared chunk URLs we can discover
    // from the current document's script tags (Vite inserts hashed asset links).
    const controller = navigator.serviceWorker.controller;
    if (controller) {
      const chunkUrls = Array.from(document.querySelectorAll("script[src]"))
        .map((el) => (el as HTMLScriptElement).src)
        .filter((src) => src.includes("/assets/") && /-[a-zA-Z0-9]{8,}\.(js|css)$/.test(src));

      if (chunkUrls.length) {
        controller.postMessage({
          type: "PREFETCH_ASSETS",
          urls: chunkUrls,
        });
      }
    }

    sessionStorage.setItem(PREFETCH_KEY, String(Date.now()));
  } catch {
    // Silently fail — prefetching is best-effort
  }
}

/**
 * Kicks off background prefetching of common studio page chunks.
 * Uses requestIdleCallback when available to stay off the critical path.
 */
export function prefetchStudioChunks(): void {
  if (!shouldRun()) return;

  const run = () => {
    doPrefetch();
  };

  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(run, { timeout: 4000 });
  } else {
    setTimeout(run, PREFETCH_DELAY_MS);
  }
}

/**
 * Mark that the user has entered the studio so future public-page
 * loads can warm studio chunks early.
 */
export function markStudioVisited(): void {
  if (typeof window !== "undefined") {
    sessionStorage.setItem("easel-studio-visited", "true");
  }
}